"use client";

import {
	Form,
	formatLocale,
	getDomainDisplayNames,
	useDialogContext,
	useNotifications,
	useZodForm,
} from "@tosize/ui";
import type { ReactNode } from "react";
import { match } from "ts-pattern";
import { createSharableCartFromId } from "~/app/[lang]/cart/actions/share";
import { useRouter } from "~/hooks/useRouter";
import { login } from "../../../../actions";
import { loginSchema } from "../../../../schemas";

interface Props {
	lang: string;
	children: ReactNode;
	redirect?: string;
	messages: {
		welcome: string;
		success: string;
		redirect: string;
	};
	onSubmit?: () => void;
	actionAfterSuccess: "close" | "redirect" | null;
}

export function ClientLoginForm(props: Props) {
	const { lang, children, redirect = "/customer/account", messages, onSubmit } = props;
	const form = useZodForm({ schema: loginSchema });
	const { setError } = form;
	const router = useRouter();
	const dialog = useDialogContext();
	const { notify } = useNotifications();

	return (
		<Form
			form={form}
			onSubmit={async (data) => {
				dialog?.setForceOpen(true);

				const response = await login({ ...data, lang });

				if (response?.serverError) {
					setError("email", { type: "value", message: response.serverError });
				} else {
					notify({
						title: response?.data?.customer?.firstname
							? `${messages.welcome} ${response.data.customer.firstname}!`
							: messages.welcome,
						subtitle: messages.success,
						variant: "success",
						action: { altText: messages.redirect, onClick: () => router.push(redirect) },
					});

					if (window.fcWidget?.isLoaded() && response?.data?.customer) {
						const sharedCartLink = response.data.cartId
							? await createSharableCartFromId({ lang, cartId: response.data.cartId })
									.catch(() => null)
									.then((result) => result?.data ?? null)
							: null;

						window.fcWidget.user.setProperties({
							locale: formatLocale(lang),
							firstName: response.data.customer.firstname,
							lastName: response.data.customer.lastname,
							email: response.data.customer.email,
							phone: response.data.customer.phone,

							"Volledige naam":
								[response.data.customer?.firstname, response.data.customer?.lastname]
									.filter(Boolean)
									.join(" ") ?? "Onbekend",
							"E-mailadres": response.data.customer.email ?? "Onbekend",
							Telefoonnummer: response.data.customer.phone ?? "Onbekend",
							"Zakelijk account":
								response.data.customer.businessStatus === "ACCEPTED" ? "Ja" : "Nee",
							"Quote ID": response.data.cartId ?? "Onbekend",
							Winkelwagen: sharedCartLink ?? "Onbekend",
							Taal: getDomainDisplayNames(lang).language ?? "Onbekend",
						});
					}

					if (onSubmit) {
						return onSubmit();
					}

					match(props.actionAfterSuccess)
						.with("close", () => dialog?.onOpenChange?.(false))
						.with("redirect", () => {
							const redirectFallback = "/customer/account";
							const url = props.redirect ?? redirectFallback;

							if (url) router.push(url);
						});
				}

				dialog?.setForceOpen(false);
			}}
			className="flex flex-col gap-4"
		>
			{children}
		</Form>
	);
}
